// UserContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { GOOGLE_SHEETS_SCOPE } from '../components/secrets';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const refreshIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false); // Prevent concurrent refreshes

  // Check if token needs refresh (within 10 minutes of expiry)
  const needsRefresh = useCallback((userData) => {
    if (!userData || !userData.loginTime) return false;
    const tokenAge = (Date.now() - userData.loginTime) / 1000; // Age in seconds
    const expiresIn = userData.expiresIn || 3600;
    const timeRemaining = expiresIn - tokenAge;
    // Refresh if less than 10 minutes remaining
    return timeRemaining < 600; // 600 seconds = 10 minutes
  }, []);

  // Check if token is expired
  const isTokenExpired = useCallback((userData) => {
    if (!userData || !userData.loginTime) return true;
    const tokenAge = (Date.now() - userData.loginTime) / 1000;
    const expiresIn = userData.expiresIn || 3600;
    return tokenAge > expiresIn;
  }, []);

  // Silent token refresh function using useGoogleLogin
  const silentRefresh = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        console.log('🔄 Token refreshed successfully');

        // Get user info with the new token
        const userInfoResponse = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          }
        );

        if (userInfoResponse.ok) {
          const userInfo = await userInfoResponse.json();

          const userData = {
            name: userInfo.name,
            email: userInfo.email,
            picture: userInfo.picture,
            accessToken: tokenResponse.access_token,
            expiresIn: tokenResponse.expires_in || 3600,
            tokenType: tokenResponse.token_type,
            scope: tokenResponse.scope,
            loginTime: Date.now(),
          };

          setCurrentUser(userData);
          localStorage.setItem('financeAppUser', JSON.stringify(userData));
          console.log('✅ Token refreshed and saved. Valid for another hour.');

          isRefreshingRef.current = false;
        }
      } catch (err) {
        console.error('Failed to refresh token:', err);
        isRefreshingRef.current = false;
        // If refresh fails, log the user out
        logout();
      }
    },
    onError: (error) => {
      console.error("Token refresh failed:", error);
      isRefreshingRef.current = false;
      // If refresh fails, log the user out
      logout();
    },
    scope: GOOGLE_SHEETS_SCOPE,
    prompt: '', // Don't show consent screen for refresh
  });

  // Refresh token if needed
  const refreshTokenIfNeeded = useCallback(() => {
    // Prevent concurrent refresh attempts
    if (isRefreshingRef.current) {
      console.log('⏳ Token refresh already in progress, skipping...');
      return;
    }

    const storedUser = localStorage.getItem('financeAppUser');
    if (!storedUser) return;

    try {
      const userData = JSON.parse(storedUser);

      // Check if token is expired first
      if (isTokenExpired(userData)) {
        console.log('❌ Token expired, logging out...');
        logout();
        return;
      }

      // Check if token needs refresh
      if (needsRefresh(userData)) {
        console.log('⚡ Token needs refresh, refreshing now...');
        isRefreshingRef.current = true;
        silentRefresh();
      }
    } catch (error) {
      console.error('Error checking token:', error);
    }
  }, [needsRefresh, isTokenExpired, silentRefresh]);

  // Setup automatic token refresh (Desktop: runs in foreground)
  const setupTokenRefresh = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Refresh token every 50 minutes (3000 seconds)
    // This gives us a 10-minute buffer before the 1-hour expiration
    const refreshInterval = 50 * 60 * 1000; // 50 minutes in milliseconds

    refreshIntervalRef.current = setInterval(() => {
      console.log('⏰ Timer-based auto-refresh triggered');
      refreshTokenIfNeeded();
    }, refreshInterval);

    console.log('🔔 Token refresh timer set (every 50 minutes)');
  }, [refreshTokenIfNeeded]);

  // Handle page visibility change (Mobile: when app comes to foreground)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Page became visible, checking token...');
        refreshTokenIfNeeded();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshTokenIfNeeded]);

  // Handle page focus (Additional check for desktop/mobile)
  useEffect(() => {
    const handleFocus = () => {
      console.log('🎯 Window focused, checking token...');
      refreshTokenIfNeeded();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshTokenIfNeeded]);

  // Load user from localStorage on mount
  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem('financeAppUser');
        if (storedUser) {
          const userData = JSON.parse(storedUser);

          // Check if token is expired
          if (isTokenExpired(userData)) {
            console.log('❌ Token expired on load, clearing localStorage');
            localStorage.removeItem('financeAppUser');
            setIsLoading(false);
            return;
          }

          // Token is valid
          setCurrentUser(userData);
          setIsLoggedIn(true);

          const tokenAge = (Date.now() - userData.loginTime) / 1000;
          console.log('✅ User loaded from localStorage:', userData.name);
          console.log('⏱️ Token age:', Math.floor(tokenAge / 60), 'minutes');

          // Setup auto-refresh for restored session
          setupTokenRefresh();

          // If token needs refresh, do it immediately
          if (needsRefresh(userData)) {
            console.log('⚡ Token needs refresh on load, refreshing immediately...');
            isRefreshingRef.current = true;
            silentRefresh();
          }
        }
      } catch (error) {
        console.error('Error loading user from localStorage:', error);
        localStorage.removeItem('financeAppUser');
      } finally {
        setIsLoading(false);
      }
    };

    loadUser();

    // Cleanup interval on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [setupTokenRefresh, needsRefresh, isTokenExpired, silentRefresh]);

  const login = (userData) => {
    setCurrentUser(userData);
    setIsLoggedIn(true);
    localStorage.setItem('financeAppUser', JSON.stringify(userData));

    // Setup auto-refresh for new session
    setupTokenRefresh();
  };

  const logout = () => {
    setCurrentUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem('financeAppUser');

    // Clear refresh interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    isRefreshingRef.current = false;
  };

  // Exposed method to proactively check token before API calls
  const ensureValidToken = useCallback(async () => {
    if (!currentUser) return false;

    if (isTokenExpired(currentUser)) {
      console.log('❌ Token expired during operation');
      logout();
      return false;
    }

    if (needsRefresh(currentUser) && !isRefreshingRef.current) {
      console.log('⚡ Proactive token refresh before API call');
      isRefreshingRef.current = true;
      silentRefresh();
      // Wait a bit for refresh to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    return true;
  }, [currentUser, isTokenExpired, needsRefresh, silentRefresh]);

  return (
    <UserContext.Provider value={{
      currentUser,
      isLoggedIn,
      isLoading,
      login,
      logout,
      setCurrentUser,
      ensureValidToken // New method for components to use before API calls
    }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the user context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
